import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useOwnerDashboard } from "../hooks/useOwnerDashboard";
import { useAuth } from "../context/AuthContext";
import { OwnerTone } from "../types/owner.types";
import { OwnerFirmUser, ownerService } from "../services/ownerService";
import { isValidEmail } from "../utils/validation";
import UserMenu from "../ui/components/UserMenu";

type OwnerSection = "dashboard" | "users" | "settings";
type UserSortField = "name" | "username" | "role" | "email" | "status" | "createdAt";
type UserStatusFilter = "all" | "active" | "inactive";
type AddUserRole = "ASSISTANT" | "ADVISOR";

interface AddUserFormState {
  role: AddUserRole;
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  phone: string;
  isActive: boolean;
}

const ownerSections: Array<{ key: OwnerSection; label: string; icon: string }> = [
  { key: "dashboard", label: "Dashboard", icon: "ti-layout-dashboard" },
  { key: "users", label: "Users", icon: "ti-users-group" },
  { key: "settings", label: "Settings", icon: "ti-settings" },
];

const badgeClass: Record<OwnerTone, string> = {
  blue: "badge-blue",
  green: "badge-green",
  amber: "badge-amber",
  red: "badge-red",
  purple: "badge-purple",
  teal: "badge-teal",
  gray: "badge-gray",
};

const dotColor: Record<OwnerTone, string> = {
  blue: "var(--blue-500)",
  green: "var(--green-500)",
  amber: "var(--amber-600)",
  red: "var(--red-500)",
  purple: "var(--purple-600)",
  teal: "var(--teal-600)",
  gray: "var(--gray-500)",
};

const parseCompletionText = (value: string): { subject: string; detail: string } => {
  const [subject, ...rest] = value.split("—");
  const trimmedSubject = (subject ?? "").trim();
  const trimmedDetail = rest.join("—").trim();

  if (!trimmedSubject) {
    return {
      subject: "Case update",
      detail: value,
    };
  }

  return {
    subject: trimmedSubject,
    detail: trimmedDetail || "Status updated.",
  };
};

const extractApiError = (error: unknown, fallback: string): string => {
  if (
    typeof error === "object" &&
    error !== null &&
    "response" in error &&
    typeof (error as { response?: { data?: { detail?: string } } }).response?.data?.detail ===
      "string"
  ) {
    return (error as { response?: { data?: { detail?: string } } }).response?.data?.detail ?? fallback;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

const formatDateShort = (isoValue: string): string => {
  const parsed = new Date(isoValue);
  if (Number.isNaN(parsed.getTime())) return isoValue;
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  }).format(parsed);
};

const compareText = (left: string, right: string): number =>
  left.localeCompare(right, undefined, { sensitivity: "base", numeric: true });

const initialAddUserForm: AddUserFormState = {
  role: "ADVISOR",
  firstName: "",
  lastName: "",
  username: "",
  email: "",
  phone: "",
  isActive: true,
};

export default function OwnerPage() {
  const { user } = useAuth();
  const ownerFirmId = user?.firmId ?? user?.id ?? null;
  const [activeSection, setActiveSection] = useState<OwnerSection>("dashboard");
  const [firmUsers, setFirmUsers] = useState<OwnerFirmUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [userSearch, setUserSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState<UserStatusFilter>("all");
  const [sortField, setSortField] = useState<UserSortField>("createdAt");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [addUserSubmitting, setAddUserSubmitting] = useState(false);
  const [addUserError, setAddUserError] = useState<string | null>(null);
  const [addUserForm, setAddUserForm] = useState<AddUserFormState>(initialAddUserForm);
  const { dashboard, loading, error, refresh } = useOwnerDashboard(user?.name);

  const loadFirmUsers = useCallback(async () => {
    if (!ownerFirmId) {
      setUsersError("Firm session is missing. Please sign in again.");
      setFirmUsers([]);
      return;
    }

    setUsersLoading(true);
    try {
      setUsersError(null);
      const data = await ownerService.getFirmUsers(ownerFirmId);
      setFirmUsers(data);
    } catch (apiError) {
      setUsersError(extractApiError(apiError, "Could not load firm users."));
      setFirmUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, [ownerFirmId]);

  useEffect(() => {
    if (activeSection !== "users") return;
    void loadFirmUsers();
  }, [activeSection, loadFirmUsers]);

  const roleOptions = useMemo(
    () => Array.from(new Set(firmUsers.map((item) => item.role))).sort(compareText),
    [firmUsers],
  );

  const filteredSortedUsers = useMemo(() => {
    const needle = userSearch.trim().toLowerCase();
    const filtered = firmUsers.filter((item) => {
      const matchesSearch =
        needle.length === 0 ||
        item.name.toLowerCase().includes(needle) ||
        item.username.toLowerCase().includes(needle) ||
        item.email.toLowerCase().includes(needle) ||
        (item.phone ?? "").toLowerCase().includes(needle);
      const matchesRole = roleFilter === "all" || item.role === roleFilter;
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" ? item.isActive : !item.isActive);
      return matchesSearch && matchesRole && matchesStatus;
    });

    const sorted = [...filtered].sort((left, right) => {
      let value = 0;
      if (sortField === "name") value = compareText(left.name, right.name);
      if (sortField === "username") value = compareText(left.username, right.username);
      if (sortField === "role") value = compareText(left.role, right.role);
      if (sortField === "email") value = compareText(left.email, right.email);
      if (sortField === "status") {
        value = compareText(left.isActive ? "Active" : "Inactive", right.isActive ? "Active" : "Inactive");
      }
      if (sortField === "createdAt") {
        const leftDate = new Date(left.createdAt).getTime();
        const rightDate = new Date(right.createdAt).getTime();
        if (Number.isNaN(leftDate) || Number.isNaN(rightDate)) {
          value = compareText(left.createdAt, right.createdAt);
        } else {
          value = leftDate - rightDate;
        }
      }
      return sortDirection === "asc" ? value : -value;
    });

    return sorted;
  }, [firmUsers, roleFilter, sortDirection, sortField, statusFilter, userSearch]);
  const addUserEmailInvalid = addUserForm.email.trim().length > 0 && !isValidEmail(addUserForm.email);

  const openAddUserModal = () => {
    setAddUserForm(initialAddUserForm);
    setAddUserError(null);
    setIsAddUserModalOpen(true);
  };

  const closeAddUserModal = () => {
    if (addUserSubmitting) return;
    setIsAddUserModalOpen(false);
    setAddUserError(null);
  };

  const handleCreateUser = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!ownerFirmId) {
      setAddUserError("Firm session is missing. Please sign in again.");
      return;
    }
    if (!isValidEmail(addUserForm.email)) {
      setAddUserError("Please enter a valid email.");
      return;
    }

    try {
      setAddUserSubmitting(true);
      setAddUserError(null);
      await ownerService.createFirmUser(ownerFirmId, {
        role: addUserForm.role,
        firstName: addUserForm.firstName,
        lastName: addUserForm.lastName,
        username: addUserForm.username,
        email: addUserForm.email,
        phone: addUserForm.phone,
        isActive: addUserForm.isActive,
      });
      await loadFirmUsers();
      setIsAddUserModalOpen(false);
      setAddUserForm(initialAddUserForm);
    } catch (apiError) {
      setAddUserError(
        extractApiError(apiError, "Could not create user. Please verify details and try again."),
      );
    } finally {
      setAddUserSubmitting(false);
    }
  };

  const renderDashboardSection = () => {
    if (!dashboard) {
      if (loading) {
        return <section className="card">Loading firm overview...</section>;
      }

      return (
        <section className="notice notice-red">
          <i className="ti ti-alert-triangle" />
          <span>
            {error ?? "Could not load owner dashboard."}{" "}
            <button
              className="btn btn-sm"
              onClick={() => {
                void refresh();
              }}
            >
              Retry
            </button>
          </span>
        </section>
      );
    }

    return (
      <>
        <div className="page-hd">
          <div className="page-title">{dashboard.header.title}</div>
          <div className="page-sub">{dashboard.header.subtitle}</div>
        </div>

        {error ? (
          <section className="notice notice-amber">
            <i className="ti ti-alert-circle" />
            <span>{error}</span>
          </section>
        ) : null}

        <section className="owner-overview-card card">
          <div className="owner-overview-head">
            <div>
              <div className="owner-overview-eyebrow">Firm overview</div>
              <h2>Performance and delivery snapshot</h2>
            </div>
            <span className="badge badge-blue">
              <i className="ti ti-calendar-week" /> Last 30 days
            </span>
          </div>
        </section>

        <section className="kpi-grid">
          {dashboard.kpis.map((kpi) => (
            <div className="kpi" key={kpi.label}>
              <div className={`kpi-icon ${kpi.tone}`}>
                <i className={`ti ${kpi.icon}`} />
              </div>
              <div className="kpi-val">{kpi.value}</div>
              <div className="kpi-label">{kpi.label}</div>
              <div className={`kpi-delta ${kpi.trend}`}>
                {kpi.trend === "up" ? (
                  <i className="ti ti-trending-up" style={{ fontSize: 12 }} />
                ) : null}
                {kpi.trend === "danger" ? (
                  <i className="ti ti-alert-triangle" style={{ fontSize: 12 }} />
                ) : null}
                {kpi.trend === "warn" ? (
                  <i className="ti ti-alert-circle" style={{ fontSize: 12 }} />
                ) : null}
                {kpi.delta}
              </div>
            </div>
          ))}
        </section>

        <div className="owner-main-grid">
          <section className="card">
            <div className="card-hd">
              <i className="ti ti-chart-bar" /> Case by stage
            </div>
            {dashboard.stageDistribution.rows.map((row) => (
              <div className="bar-row" key={row.label}>
                <div className="bar-lbl">{row.label}</div>
                <div className="bar-outer">
                  <div
                    className="bar-fill"
                    style={{ width: `${row.width}%`, background: row.color }}
                  />
                </div>
                <div className="bar-count">{row.count}</div>
              </div>
            ))}

            <div className="divider" />
            <div className="owner-summary-grid">
              {dashboard.stageDistribution.summaryCards.map((card) => (
                <div key={card.label} className={`owner-summary owner-summary-${card.tone}`}>
                  <div className="owner-summary-value">{card.value}</div>
                  <div className="owner-summary-label">{card.label}</div>
                </div>
              ))}
            </div>
          </section>

          <section className="card">
            <div className="card-hd">
              <i className="ti ti-users" /> Advisor workload
            </div>
            {dashboard.workload.advisers.map((adviser) => (
              <div className="wl-row" key={adviser.id}>
                <div className={`avatar av-${adviser.avatarTone}`}>{adviser.initials}</div>
                <div className="wl-info">
                  <div className="wl-name">{adviser.name}</div>
                  <div className="wl-bars">
                    <div className="prog-labels">
                      <span>{adviser.activeCases} active cases</span>
                      <span style={{ color: `var(--${adviser.overdueTone}-600)` }}>
                        {adviser.overdueText}
                      </span>
                    </div>
                    <div className="prog-bar">
                      <div
                        className={`prog-fill ${adviser.barTone === "green" ? "green" : ""}`}
                        style={{ width: `${adviser.loadPercent}%` }}
                      />
                    </div>
                  </div>
                </div>
                <span className={`badge ${badgeClass[adviser.badgeTone]}`}>
                  {adviser.badgeLabel}
                </span>
              </div>
            ))}
          </section>
        </div>

        <section className="card">
          <div className="card-hd">
            <i className="ti ti-timeline" /> Recent case completion
          </div>
          <div className="owner-completions-list">
            {dashboard.completions.items.map((item, index) => {
              const parsed = parseCompletionText(item.text);
              return (
                <div
                  className="owner-completion-row"
                  key={item.id}
                  style={index === dashboard.completions.items.length - 1 ? { borderBottom: "none" } : undefined}
                >
                  <div className="owner-completion-left">
                    <div className="tl-dot-col">
                      <div className="tl-dot" style={{ background: dotColor[item.dotTone] }} />
                    </div>
                    <div className="owner-completion-body">
                      <div className="owner-completion-title">{parsed.subject}</div>
                      <div className="owner-completion-sub">{parsed.detail}</div>
                    </div>
                  </div>
                  <div className="owner-completion-right">
                    <span className={`badge ${badgeClass[item.badgeTone]}`}>{item.badgeLabel}</span>
                    <span className="owner-completion-time">{item.time}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </>
    );
  };

  const renderUsersSection = () => (
    <section className="card">
      <div className="owner-users-head">
        <div>
          <div className="owner-overview-eyebrow">Firm users</div>
          <div className="owner-users-title">Users</div>
        </div>
        <button type="button" className="btn btn-primary btn-sm" onClick={openAddUserModal}>
          <i className="ti ti-plus" />
          Add user
        </button>
      </div>

      <div className="owner-users-controls">
        <div className="owner-users-search">
          <i className="ti ti-search" />
          <input
            type="text"
            value={userSearch}
            onChange={(event) => setUserSearch(event.target.value)}
            placeholder="Search by name, username, email, phone"
          />
        </div>

        <select
          value={roleFilter}
          onChange={(event) => setRoleFilter(event.target.value)}
          aria-label="Filter users by role"
        >
          <option value="all">All roles</option>
          {roleOptions.map((role) => (
            <option key={role} value={role}>
              {role}
            </option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value as UserStatusFilter)}
          aria-label="Filter users by status"
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>

        <select
          value={sortField}
          onChange={(event) => setSortField(event.target.value as UserSortField)}
          aria-label="Sort users by field"
        >
          <option value="createdAt">Sort: Created</option>
          <option value="name">Sort: Name</option>
          <option value="username">Sort: Username</option>
          <option value="role">Sort: Role</option>
          <option value="email">Sort: Email</option>
          <option value="status">Sort: Status</option>
        </select>

        <button
          type="button"
          className="btn btn-sm owner-users-sort-btn"
          onClick={() => setSortDirection((previous) => (previous === "asc" ? "desc" : "asc"))}
          aria-label="Toggle sort direction"
        >
          <i className={`ti ${sortDirection === "asc" ? "ti-sort-ascending" : "ti-sort-descending"}`} />
          {sortDirection === "asc" ? "Asc" : "Desc"}
        </button>
      </div>

      <div className="owner-users-meta">
        Showing {filteredSortedUsers.length} of {firmUsers.length} users
      </div>

      {usersError ? (
        <section className="notice notice-red">
          <i className="ti ti-alert-triangle" />
          <span>{usersError}</span>
        </section>
      ) : null}

      {usersLoading ? (
        <div className="owner-empty-sub">Loading users...</div>
      ) : null}

      {!usersLoading && !usersError ? (
        firmUsers.length === 0 ? (
          <div className="owner-empty-sub">No users found for this firm.</div>
        ) : filteredSortedUsers.length === 0 ? (
          <div className="owner-empty-sub">No users match the current filters.</div>
        ) : (
          <div className="owner-users-table-wrap">
            <table className="owner-users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Username</th>
                  <th>Role</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {filteredSortedUsers.map((item) => (
                  <tr key={item.id}>
                    <td>{item.name}</td>
                    <td>{item.username}</td>
                    <td>{item.role}</td>
                    <td>{item.email}</td>
                    <td>{item.phone ?? "—"}</td>
                    <td>
                      <span className={`badge ${item.isActive ? "badge-green" : "badge-gray"}`}>
                        {item.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>{formatDateShort(item.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}
    </section>
  );

  const renderAddUserModal = () => {
    if (!isAddUserModalOpen) return null;

    return (
      <div className="assistant-modal-overlay" onClick={closeAddUserModal}>
        <section
          className="assistant-modal card owner-add-user-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Add firm user"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="assistant-modal-header">
            <div>
              <h3>Create user</h3>
              <div className="owner-add-user-subtitle">
                Add an Assistant or Adviser under this firm.
              </div>
            </div>
            <button type="button" className="btn btn-sm" onClick={closeAddUserModal} disabled={addUserSubmitting}>
              <i className="ti ti-x" />
            </button>
          </div>

          {addUserError ? (
            <section className="notice notice-red owner-add-user-error">
              <i className="ti ti-alert-triangle" />
              <span>{addUserError}</span>
            </section>
          ) : null}

          <form onSubmit={handleCreateUser}>
            <div className="field-group">
              <div className="field">
                <label htmlFor="owner-add-user-role">Role *</label>
                <select
                  id="owner-add-user-role"
                  value={addUserForm.role}
                  onChange={(event) =>
                    setAddUserForm((previous) => ({
                      ...previous,
                      role: event.target.value as AddUserRole,
                    }))
                  }
                  required
                >
                  <option value="ADVISOR">Adviser</option>
                  <option value="ASSISTANT">Assistant</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="owner-add-user-username">Username *</label>
                <input
                  id="owner-add-user-username"
                  type="text"
                  value={addUserForm.username}
                  onChange={(event) =>
                    setAddUserForm((previous) => ({
                      ...previous,
                      username: event.target.value,
                    }))
                  }
                  placeholder="jane.doe"
                  minLength={3}
                  maxLength={60}
                  required
                />
              </div>
            </div>

            <div className="field-group">
              <div className="field">
                <label htmlFor="owner-add-user-first-name">First name *</label>
                <input
                  id="owner-add-user-first-name"
                  type="text"
                  value={addUserForm.firstName}
                  onChange={(event) =>
                    setAddUserForm((previous) => ({
                      ...previous,
                      firstName: event.target.value,
                    }))
                  }
                  maxLength={120}
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="owner-add-user-last-name">Last name *</label>
                <input
                  id="owner-add-user-last-name"
                  type="text"
                  value={addUserForm.lastName}
                  onChange={(event) =>
                    setAddUserForm((previous) => ({
                      ...previous,
                      lastName: event.target.value,
                    }))
                  }
                  maxLength={120}
                  required
                />
              </div>
            </div>

            <div className="field-group">
              <div className="field">
                <label htmlFor="owner-add-user-email">Email *</label>
                <input
                  id="owner-add-user-email"
                  type="email"
                  value={addUserForm.email}
                  className={addUserEmailInvalid ? "input-invalid" : undefined}
                  aria-invalid={addUserEmailInvalid}
                  onChange={(event) =>
                    setAddUserForm((previous) => ({
                      ...previous,
                      email: event.target.value,
                    }))
                  }
                  maxLength={255}
                  required
                />
                {addUserEmailInvalid ? (
                  <div className="field-inline-error">Enter a valid email address.</div>
                ) : null}
              </div>
              <div className="field">
                <label htmlFor="owner-add-user-phone">Phone</label>
                <input
                  id="owner-add-user-phone"
                  type="text"
                  value={addUserForm.phone}
                  onChange={(event) =>
                    setAddUserForm((previous) => ({
                      ...previous,
                      phone: event.target.value,
                    }))
                  }
                  maxLength={40}
                />
              </div>
            </div>

            <label className="owner-add-user-active">
              <input
                type="checkbox"
                checked={addUserForm.isActive}
                onChange={(event) =>
                  setAddUserForm((previous) => ({
                    ...previous,
                    isActive: event.target.checked,
                  }))
                }
              />
              Create as active user
            </label>

            <div className="assistant-modal-actions">
              <button type="button" className="btn btn-sm" onClick={closeAddUserModal} disabled={addUserSubmitting}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={addUserSubmitting}>
                {addUserSubmitting ? "Creating..." : "Create user"}
              </button>
            </div>
          </form>
        </section>
      </div>
    );
  };

  const renderEmptySection = (section: "users" | "settings") => (
    <section className="card owner-empty">
      <div className="owner-empty-title">
        <i className={`ti ${section === "users" ? "ti-users-group" : "ti-settings"}`} />
        {section === "users" ? "Users" : "Settings"}
      </div>
      <div className="owner-empty-sub">
        {section === "users"
          ? "Users section is empty for now."
          : "Settings section is empty for now."}
      </div>
    </section>
  );

  return (
    <div className="app">
      <nav className="topnav">
        <div className="logo">
          <div className="logo-mark">M</div>
          CaseFlow
        </div>
        <div className="nav-right">
          <span className="badge badge-purple">
            <i className="ti ti-chart-bar" /> Firm owner
          </span>
          <UserMenu initials="FO" />
        </div>
      </nav>

      <div className="page active">
        <div className="owner-layout">
          <div className="owner-shell">
            <aside className="owner-side card">
              <div className="owner-side-head">Workspace</div>
              <div className="owner-side-nav">
                {ownerSections.map((section) => (
                  <button
                    key={section.key}
                    className={`owner-side-item ${activeSection === section.key ? "active" : ""}`}
                    onClick={() => setActiveSection(section.key)}
                  >
                    <i className={`ti ${section.icon}`} />
                    {section.label}
                  </button>
                ))}
              </div>
            </aside>

            <section className="owner-content">
              {activeSection === "dashboard" ? renderDashboardSection() : null}
              {activeSection === "users" ? renderUsersSection() : null}
              {activeSection === "settings" ? renderEmptySection("settings") : null}
            </section>
          </div>
        </div>
      </div>
      {renderAddUserModal()}
    </div>
  );
}
